'use client';
import { useState, useEffect, use } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Layers, Calendar, User, Tag, FileText, Lock } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import styles from './extracted.module.css';

export const dynamic = 'force-dynamic';

export default function ExtractedBrandsPage({ params }) {
    const resolvedParams = use(params);
    const jobId = resolvedParams.jobId;
    const router = useRouter();

    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBrands = async () => {
            const { data, error } = await supabase
                .from('pdf_brands')
                .select('*')
                .eq('job_id', jobId)
                .order('name');

            if (error) {
                console.error('Error fetching brands:', error);
            } else {
                setBrands(data || []);
            }
            setLoading(false);
        };

        if (jobId) {
            fetchBrands();
        }
    }, [jobId]);

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <div>
                    <button onClick={() => router.back()} className={styles.backBtn}>
                        <ArrowLeft size={16} /> Back to History
                    </button>
                    <div className={styles.titleRow}>
                        <Layers size={28} />
                        <div>
                            <h1 className={styles.title}>Extracted Brands</h1>
                            <p className={styles.subtitle}>Brands detected in the scanned document</p>
                        </div>
                    </div>
                </div>
            </header>

            <div className={styles.grid}>
                {loading ? (
                    <div className={styles.loading}>Loading extracted brands...</div>
                ) : brands.length === 0 ? (
                    <div className={styles.empty}>No brands found in this scan.</div>
                ) : (
                    brands.map(brand => (
                        <div key={brand.id} className={styles.card}>
                            <div className={styles.headerRow}>
                                <div className={styles.logoContainer}>
                                    {brand.logo_path ? (
                                        <img src={brand.logo_path} alt={`${brand.name} logo`} className={styles.logo} />
                                    ) : (
                                        <div className={styles.placeholderLogo}>No Logo</div>
                                    )}
                                </div>
                                <div className={styles.brandInfo}>
                                    <h3 className={styles.brandName}>{brand.name}</h3>
                                    {brand.application_number && (
                                        <span className={styles.appNumber}>
                                            <Lock size={12} style={{ marginRight: 4, display: 'inline' }} />
                                            {brand.application_number}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className={styles.detailsGrid}>
                                <div className={styles.detailItem}>
                                    <span className={styles.label}><Calendar size={12} /> Filing Date</span>
                                    <span className={styles.value}>{brand.filing_date || 'N/A'}</span>
                                </div>
                                <div className={styles.detailItem}>
                                    <span className={styles.label}><Calendar size={12} /> Expiry Date</span>
                                    <span className={styles.value}>{brand.expiration_date || 'N/A'}</span>
                                </div>
                                <div className={styles.detailItem}>
                                    <span className={styles.label}><User size={12} /> Owner</span>
                                    <span className={styles.value}>{brand.owner || 'N/A'}</span>
                                </div>
                                <div className={styles.detailItem}>
                                    <span className={styles.label}><Tag size={12} /> Nice Class</span>
                                    <span className={styles.value}>{brand.nice_class || 'N/A'}</span>
                                </div>
                                <div className={styles.detailItem}>
                                    <span className={styles.label}>Colors</span>
                                    <span className={styles.value}>{brand.colors || 'N/A'}</span>
                                </div>
                            </div>

                            {brand.description && (
                                <div className={styles.description}>
                                    <div className={styles.label} style={{ marginBottom: 4 }}><FileText size={12} /> Visual Description</div>
                                    <p className={styles.descText}>{brand.description}</p>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
